import { useEffect, useState } from 'react';
import { useLocation,useParams } from 'react-router-dom';

import config from '@/config';
import useStateHandler from "@/hooks/useStateHandler";
import PostNotFound from '@/pages/profile/post/components/PostNotFound';
import PostTags from '@/pages/profile/post/post-tags';
import GalleryService from '@/services/gallery.service';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import ArticleComments from '@/shared-components/forms/add-board-content/add-article/published-article/ArticleComments';
import ArticleInfo, { ArticleHeader } from '@/shared-components/forms/add-board-content/add-article/published-article/ArticleInfo';
import CommentsPreview from '@/shared-components/forms/add-board-content/add-article/published-article/CommentsPreview';
import RecommendedArticlesList from '@/shared-components/forms/add-board-content/add-article/published-article/RecommendedArticlesList';
import ContentMetaDesc from '@/shared-components/forms/add-board-content/shared-components/ContentMetaDesc';
import Header from '@/shared-components/forms/add-board-content/shared-components/outside-header';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from "@/shared-components/loader/Loader";
import GenericBottomSheet from '@/shared-components/popups/components/GenericBottomSheet';
import { getSsrContentBootstrap } from '@/utils/ssrContentBootstrap';

import './article.css';

export default function Article() {
  const { articleId, postId } = useParams();
  const resolvedArticleId = articleId || postId;
  const { cacheHandler } = useStateHandler();
  const ssrBootstrap = getSsrContentBootstrap(resolvedArticleId);

  const location = useLocation();
  const { scrollY, slideIndex } = location.state || {};
  // Like state
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked]   = useState(false);

  // Enhanced: allow forceFresh to always get latest from server if needed
  const [{
    loading,
    error,
    data,
  }, setArticleFetched] = useState({
    loading: !ssrBootstrap?.data,
    error: ssrBootstrap?.forceNotFound === true,
    data: ssrBootstrap?.data ?? null,
  });

  useEffect(() => {
    if (!resolvedArticleId || ssrBootstrap?.data) {
      return;
    }

    GalleryService.getPublicContentWithSeoById(resolvedArticleId)
      .then(response => {
        setArticleFetched({
          loading: false,
          data: response.data,
          error: null,
        });
        setLikeCount(response.data.post_like_count || 0);
        setIsLiked(response.data.is_liked || false);
        setCommentCount(response.data.comment_count || 0);
      })
      .catch(() => {
        setArticleFetched({
          loading: false,
          data: null,
          error: true,
        });
      });

  }, [resolvedArticleId, ssrBootstrap?.data]);

  // ← NEW: once loading is done, restore scroll if we have a saved Y
  useEffect(() => {
    if (!loading && scrollY != null) {
      // delay until after paint
      setTimeout(() => window.scrollTo(0, scrollY), 0);
    }
  }, [loading, scrollY]);

  const updateArticlesCache = (updateFn) => {
    // we change the valuee of the cache
    const cacheKey = `${owner.id || owner._id}_articles`;
    const currentArticles = cacheHandler.getCacheValue(cacheKey);
    if (currentArticles) {
      // update the like count in the cached posts
      const updatedArticles = {
        ...currentArticles,
        data: currentArticles.data.data.map(updateFn),
      };
      cacheHandler.storeInCache(cacheKey, updatedArticles);
    }
  };

  // Like handler (no extra throttle—just busy flag)
  const handleLike = async newStatus => {
    let updatedCount;
    try {
      if (newStatus) {
        updatedCount = likeCount + 1;
      } else {
        updatedCount = likeCount > 0 ? likeCount - 1 : 0;
      }
      setLikeCount(updatedCount);
      setIsLiked(newStatus);
    } catch (err) {
      console.error("Failed to update like on server", err);
      // (optional) roll back if you really care about consistency
    } finally {
      updateArticlesCache(article => {
        if (article._id === data._id) {
          return { ...article, post_like_count: updatedCount, is_liked: newStatus };
        }
        return article;
      });
    }
  };

  const [isCommentsOpen, setIsCommentsOpen] = useState(location.state?.openComments || false);
  const [previewReplyTo, setPreviewReplyTo] = useState(false);

  const [commentCount, setCommentCount]   = useState(0);

  const onCommentAdded = () => {
    const newCount = commentCount + 1;
    setCommentCount(newCount);

    // Update the cache for this article
    updateArticlesCache(article => {
      if (article._id === data._id) {
        return { ...article, comment_count: newCount };
      }
      return article;
    });
  };

  if (loading) {
    return (
      <>
        <Loader/>
      </>
    );
  }

  if (error) {
    return <PostNotFound mode="article" />;
  }

  // now that we're past loading/error, data is non-null:
  const owner = data.gallery || data.artist;

  // Pull out comment_count and recommendedArticles for convenience:
  const recommended  = (data.post_container?.recommendedArticles) || [];

  // ← add key={articleId} here so that changing `articleId` forces a full remount
  return (
    <div key={resolvedArticleId} className="article__container">
      <ContentMetaDesc content={data} />
       
      <Header fetchData={data}/>
      <main className="article__main">
        <ArticleHeader 
          fetchData={data}
        />

        {/* Title & Description */}
        <h1 className="article__title">
          {data.post_container.post_title}
        </h1>
        {data.post_container.post_description && (
          <p className="post__short-description">
            {data.post_container.post_description}
          </p>
        )}

        {/* Cover + Content */}
        <div className="article__body-row">
          <div className="article__cover-wrapper">
            {data.post_container.post_cover?.image_id ? (
              <img
                className="article__cover"
                src={`${config.app.imageServiceDomain}/${data.post_container.post_cover.image_id}/w=300`}
                alt="Article cover"
              />
            ) : (
              <div className="article__cover article__cover--placeholder" role="presentation">
                <span className="article__cover-placeholder__label">
                  No cover image
                </span>
              </div>
            )}
            <div className="article__cover-actions">
              {/* Cover LikeButton */}
              <LikeButton
                itemId={data._id}
                typeOfItem="POST"
                isLiked={isLiked}
                onLikeAction={handleLike}
                className="article__cover-like-button"
              />
            </div>
          </div>

          <div className="article__content">
            {/* 1) ArticleInfo (renders body + footer) */}
            <ArticleInfo
              fetchData={data}
              likeCount={likeCount}
              isLiked={isLiked}
              onLike={handleLike}
              commentCount={commentCount}
              onOpenComments={() => setIsCommentsOpen(true)}
              initialSlideIndex={slideIndex}
              isPublishedView
            />

            {/* 2) If there are no comments at all, show recommendations here */}
            {commentCount === 0 && (
              <RecommendedArticlesList articles={recommended} />
            )}

            {/* 3) Post tags */}
            <PostTags
              postTags={data.post_container.post_tags}
              fetchData={data}
            />

            {/* 4) Inline preview of first few comments (only when sheet is closed) */}
            {!isCommentsOpen && (
              <>
                <CommentsPreview
                  articleId={data._id}
                  commentCount={commentCount}
                  onOpenSheet={() => {
                    setPreviewReplyTo(null);
                    setIsCommentsOpen(true);
                  }}
                  onReplyPreview={comment => {
                    setPreviewReplyTo(comment);
                    setIsCommentsOpen(true);
                  }}
                />
                {/* 5) If there _are_ comments, place recommendations beneath the preview */}
                {commentCount > 0 && (
                  <RecommendedArticlesList
                    articles={data.post_container.recommendedArticles}
                  />                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* ——— Full comments in a bottom sheet ——— */}
      <GenericBottomSheet
        open={isCommentsOpen}
        close={() => setIsCommentsOpen(false)}
        backgroundClassName="article-comments-background"
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
        className="article-comments-bottom-sheet"
      >
        <div className="artwork-view-socials-header__container">
          <span className="artwork-view-socials-header__title">Comments</span>
          <XIcon
            className="artwork-view-socials-header__close"
            onClick={() => setIsCommentsOpen(false)}
          />
        </div>

        <div className="article-comments-sheet__content">
          <ArticleComments
            articleId={data._id}
            initialReplyTo={previewReplyTo}
            onCommentAdded={onCommentAdded}
          />
        </div>
      </GenericBottomSheet>
    </div>
  );
}
